import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router, Routes } from '@angular/router';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { MAIN_SPACE_CONTEXT_VARIANT, readSpaceContextSlot } from '@frontendlib/common/utils/space-context-storage';
import { storageAccountId } from '@frontendlib/common/utils/storage-account';
import { PAY_NOW_QUERY_PARAM } from '@modules/settings/components/general/pay-now-dialog/pay-now-dialog';
import { Space } from '@modules/settings/types/space';
import { SettingsStubPageComponent } from '@modules/settings-window/settings-stub-page.component';
import {
  isLegacySettingsMode,
  LEGACY_SETTINGS_SESSION_KEY,
  resolveSettingsRole,
  SETTINGS_WINDOW_OPENED_IN_APP_STATE,
  setLegacySettingsMode,
  SettingsWindowOpenerService,
} from '@modules/settings-window/settings-window-opener.service';
import { settingsVisiblePaths, type SettingsAudience } from '@modules/settings-window/settings-menu';
import { environment } from '@environment';
import { CHALLENGE_QUERY_PARAM } from '@sharedlib/common/three-d-secure';
import { combineLatest, from, map, Observable, of, switchMap, take } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Declared alongside the opener that clears it, so the two never disagree; re-exported here
// because this guard is what writes and reads it.
export { LEGACY_SETTINGS_SESSION_KEY };

/**
 * The window's pages come from its own lazy chunk, so `router.config` does not hold them. Importing
 * it here costs nothing extra: a redirect opens the window, which loads the same chunk.
 */
function loadSettingsWindowRoutes(): Observable<Routes> {
  return from(import('@modules/settings-window/settings-window.routes')).pipe(map((m) => m.SETTINGS_WINDOW_ROUTES));
}

/** The intersection of audience-visible shipped pages and non-stub routes mounted in the window. */
export function availableSettingsWindowPaths(
  windowRoutes: Routes,
  audience: SettingsAudience,
  teamLogin: boolean,
): ReadonlySet<string> {
  const mounted = new Set(
    windowRoutes
      .filter((route) => route.path && route.path !== '**' && route.component !== SettingsStubPageComponent)
      .map((route) => route.path as string),
  );
  const visible = settingsVisiblePaths(audience, () => true, teamLogin);

  return new Set([...visible].filter((path) => mounted.has(path)));
}

/** Maps a legacy full-screen settings path only when that page is available in this window. */
export function resolveLegacySettingsWindowPath(
  legacyPath: string,
  audience: SettingsAudience,
  availablePaths: ReadonlySet<string>,
): string | undefined {
  const path = legacyPath.replace(/^\/settings\/?/, '').replace(/\/$/, '');
  let target: string | undefined;

  if (path === 'general/usage') {
    target = audience === 'admin' || audience === 'owner' ? 'team/usage' : 'general/usage';
  } else if (path === 'general/billing' || path === 'general/plan' || path === 'general/billing/authenticate') {
    const billing = audience === 'owner' ? 'team/billing' : audience === 'personal' ? 'general/billing' : undefined;
    if (!billing || !availablePaths.has(billing)) {
      return undefined;
    }
    // The plan picker and the bank return page are Billing sub-pages, mounted wherever Billing is.
    if (path === 'general/plan') {
      return `${billing}/plan`;
    }
    return path === 'general/billing/authenticate' ? `${billing}/authenticate` : billing;
  } else if (/^(mail|calendar|tasking|notes|contacts|files)\/shared\/[^/]+$/.test(path)) {
    target = audience === 'personal' ? 'general/shared-items' : 'general/shared-workspace';
  } else if (path === 'calendar/default') {
    target = 'calendar';
  } else if (path === 'tasking/due_dates') {
    target = 'tasks';
  } else {
    target = path;
  }

  return target && availablePaths.has(target) ? target : undefined;
}

function queryParams(url: string): URLSearchParams {
  return new URLSearchParams(url.split('?', 2)[1]?.split('#', 1)[0] ?? '');
}

/** Billing's mail link carries `pay`, the bank return page its challenge; every other link drops its query. */
function keptQueryParams(target: string, url: string): Record<string, string> | null {
  const params = queryParams(url);
  if (target.endsWith('/authenticate')) {
    const challenge = params.get(CHALLENGE_QUERY_PARAM);
    return challenge ? { [CHALLENGE_QUERY_PARAM]: challenge } : null;
  }
  if (target.endsWith('/billing') && params.get(PAY_NOW_QUERY_PARAM) === '1') {
    return { [PAY_NOW_QUERY_PARAM]: '1' };
  }
  return null;
}

function requestedLegacyMode(url: string): boolean {
  return queryParams(url).get('legacy') === '1';
}

/**
 * The workspace THIS account last worked in — never whichever account booted last on the browser
 * (#1303 GP-2). The settings audience is resolved against it, so an account-blind read handed the
 * entered account its predecessor's audience straight after a switch.
 */
export function rememberedSpaceId(): string | undefined {
  return readSpaceContextSlot(MAIN_SPACE_CONTEXT_VARIANT, storageAccountId());
}

export function loadAudience(
  http: HttpClient,
  accountId: string,
  teamLogin: boolean,
): Observable<SettingsAudience | undefined> {
  const spaceId = rememberedSpaceId();
  if (!spaceId || !teamLogin) {
    return of('personal');
  }
  return http
    .get<{ space: Partial<Space> }>(`${environment.baseUrl}/api/account/spaces/${encodeURIComponent(spaceId)}`)
    .pipe(map(({ space }) => resolveSettingsRole(new Space(space), accountId)));
}

/** Redirects shipped legacy settings pages into the floating window while preserving a hard legacy escape hatch. */
export const settingsWindowDeepLinkGuard: CanActivateFn = (_route, state) => {
  if (requestedLegacyMode(state.url)) {
    setLegacySettingsMode(true);
    return true;
  }
  if (isLegacySettingsMode()) {
    return true;
  }

  const router = inject(Router);
  const legacyPath = state.url.split(/[?#]/, 1)[0];
  const initialPath = router.getCurrentNavigation()?.initialUrl.toString().split(/[?#]/, 1)[0];
  if (
    legacyPath === '/settings' ||
    legacyPath === '/settings/' ||
    initialPath === '/settings' ||
    initialPath === '/settings/'
  ) {
    // The nav-rail app switcher enters here before the old module redirects to its Account child.
    // Carry that deliberate legacy entry across the child redirect just like ?legacy=1.
    setLegacySettingsMode(true);
    return true;
  }
  if (legacyPath.startsWith('/settings/teams')) {
    return true;
  }

  const accountService = inject(AccountService);
  const accountKind = inject(AccountKindService);
  const http = inject(HttpClient);
  const opener = inject(SettingsWindowOpenerService);
  const windowRoutes$ = loadSettingsWindowRoutes();

  return combineLatest([accountService.getAccount(), accountService.getAccountInfo()]).pipe(
    take(1),
    switchMap(([account, info]) => {
      if (info.checkoutRequired || !account.id) {
        return of(true);
      }
      return loadAudience(http, account.id, accountKind.isTeam()).pipe(
        switchMap((audience) =>
          combineLatest([opener.firstAvailableHostWhenReady(), windowRoutes$]).pipe(
            map(([host, windowRoutes]) => {
              if (!audience || !host) {
                return true;
              }
              const target = resolveLegacySettingsWindowPath(
                legacyPath,
                audience,
                availableSettingsWindowPaths(windowRoutes, audience, accountKind.isTeam()),
              );
              if (!target) {
                return true;
              }
              /*
               * An in-app link never wrote the legacy URL to the browser (the deferred URL update
               * runs after guards), so the entry on screen is the app behind: keep it, and let the
               * window close by popping back onto it. A cold entry IS the legacy URL, so replace it
               * — otherwise close walks back onto `/settings/...`, which redirects here again, and
               * the window cannot be closed over a blocked page.
               */
              const commands = [{ outlets: { primary: host.split('/'), settings: target.split('/') } }];
              const kept = keptQueryParams(target, state.url);
              return new RedirectCommand(
                kept ? router.createUrlTree(commands, { queryParams: kept }) : router.createUrlTree(commands),
                router.navigated ? { state: { [SETTINGS_WINDOW_OPENED_IN_APP_STATE]: true } } : { replaceUrl: true },
              );
            }),
          ),
        ),
      );
    }),
    catchError(() => of(true)),
  );
};
